'use client'
import { GetFreeCreditLink } from '@/components/get-free-credit-button'
import { Logo } from '@/components/logo'
import { FeatureLink } from '@/components/common/feature-link'
import { LanguageSelector } from '@/components/language-selector'
import { DISCORD_URL, X_URL, LINKEDIN_URL, HELP_URL } from '@/lib/constant'
import { useLocale, useTranslations } from 'next-intl'
import Balancer from 'react-wrap-balancer'
import Link from 'next/link'
import { getLocalizedPath } from '@/lib/i18n-utils'
import type { Locale } from '@/i18n/config'
import { TwitterIcon, LinkedInIcon, DiscordIcon } from '@/components/icons'

export const FooterV3 = () => {
  const t = useTranslations('home.footer')
  const homepage2_t = useTranslations('homepage_v2.footer')
  const locale = useLocale() as Locale

  return (
    <footer className="relative overflow-hidden bg-gradient-to-b from-[#1a1a1a] via-[#212121] to-[#1a1a1a] border-t border-white/5">
      {/* Premium decorative elements */}
      <div className="absolute inset-0 pointer-events-none">
        <div className="absolute top-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-white/10 to-transparent" />
        <div className="absolute inset-0 bg-gradient-to-b from-transparent via-white/[0.015] to-transparent" />
      </div>

      {/* Main Footer */}
      <div className="relative container mx-auto px-4 sm:px-6 lg:px-8 z-10 pt-8 md:pt-10 pb-6 md:pb-8">
        <div className="max-w-7xl mx-auto">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-6 gap-6 lg:gap-8">
            {/* Logo + About - Takes 2 columns on large screens */}
            <div className="lg:col-span-2">
              <Logo variant="light" className="-ml-4 mb-4" />

              <div className="mb-4">
                <p className="text-white/95 text-base md:text-lg font-medium leading-[1.4] tracking-tight">
                  <Balancer>{homepage2_t('title')}</Balancer>
                </p>
              </div>

              {/* Social Links */}
              <div className="flex items-center gap-3 mt-4">
                <a
                  href={X_URL}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="w-16 h-16 rounded-2xl bg-gradient-to-br from-white/[0.08] to-white/[0.03] hover:from-white/[0.12] hover:to-white/[0.06] border border-white/10 hover:border-white/20 flex items-center justify-center transition-all duration-500 group shadow-xl shadow-black/30 hover:shadow-lg hover:scale-105 backdrop-blur-sm"
                  aria-label={t('social_v2.twitter')}
                >
                  <TwitterIcon className="w-8 h-8 text-white group-hover:text-white transition-all duration-500" />
                </a>
                <a
                  href={LINKEDIN_URL}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="w-16 h-16 rounded-2xl bg-gradient-to-br from-white/[0.08] to-white/[0.03] hover:from-white/[0.12] hover:to-white/[0.06] border border-white/10 hover:border-[#0077B5]/40 flex items-center justify-center transition-all duration-500 group shadow-xl shadow-black/30 hover:shadow-lg hover:scale-105 backdrop-blur-sm"
                  aria-label={t('social_v2.linkedin')}
                >
                  <LinkedInIcon className="w-8 h-8 text-[#0077B5] group-hover:text-[#0077B5] transition-all duration-500" />
                </a>
                <a
                  href={DISCORD_URL}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="w-16 h-16 rounded-2xl bg-gradient-to-br from-white/[0.08] to-white/[0.03] hover:from-white/[0.12] hover:to-white/[0.06] border border-white/10 hover:border-[#5865F2]/40 flex items-center justify-center transition-all duration-500 group shadow-xl shadow-black/30 hover:shadow-lg hover:scale-105 backdrop-blur-sm"
                  aria-label={t('social_v2.discord')}
                >
                  <DiscordIcon className="w-8 h-8 text-[#5865F2] group-hover:text-[#5865F2] transition-all duration-500" />
                </a>
              </div>
            </div>

            {/* Features */}
            <div className="lg:col-span-1">
              <h3 className="text-[13px] font-semibold mb-3 text-white/95 tracking-wide letter-spacing-wider">
                {t('features.title')}
              </h3>
              <ul className="space-y-2">
                <li>
                  <FeatureLink
                    featureSlug=""
                    className="text-white/75 hover:text-[#FDD577] flex items-center transition-all duration-300 text-[13px] leading-[1.5] font-normal hover:translate-x-1"
                  >
                    {t('features.all_features')}
                  </FeatureLink>
                </li>
                <li>
                  <FeatureLink
                    featureSlug="ai-models"
                    className="text-white/75 hover:text-[#FDD577] flex items-center transition-all duration-300 text-[13px] leading-[1.5] font-normal hover:translate-x-1"
                  >
                    {t('features.ai_models')}
                  </FeatureLink>
                </li>
                <li>
                  <FeatureLink
                    featureSlug="database"
                    className="text-white/75 hover:text-[#FDD577] flex items-center transition-all duration-300 text-[13px] leading-[1.5] font-normal hover:translate-x-1"
                  >
                    {t('features.database')}
                  </FeatureLink>
                </li>
                <li>
                  <FeatureLink
                    featureSlug="app-store"
                    className="text-white/75 hover:text-[#FDD577] flex items-center transition-all duration-300 text-[13px] leading-[1.5] font-normal hover:translate-x-1"
                  >
                    {t('features.app_store')}
                  </FeatureLink>
                </li>
                <li>
                  <FeatureLink
                    featureSlug="payments"
                    className="text-white/75 hover:text-[#FDD577] flex items-center transition-all duration-300 text-[13px] leading-[1.5] font-normal hover:translate-x-1"
                  >
                    {t('features.payments')}
                  </FeatureLink>
                </li>
                <li>
                  <FeatureLink
                    featureSlug="visual-editor"
                    className="text-white/75 hover:text-[#FDD577] flex items-center transition-all duration-300 text-[13px] leading-[1.5] font-normal hover:translate-x-1"
                  >
                    {t('features.visual_editor')}
                  </FeatureLink>
                </li>
                <li>
                  <FeatureLink
                    featureSlug="seo"
                    className="text-white/75 hover:text-[#FDD577] flex items-center transition-all duration-300 text-[13px] leading-[1.5] font-normal hover:translate-x-1"
                  >
                    {t('features.seo')}
                  </FeatureLink>
                </li>
              </ul>
            </div>

            {/* Free Tools */}
            <div className="lg:col-span-1">
              <h3 className="text-[13px] font-semibold mb-3 text-white/95 tracking-wide letter-spacing-wider">
                {t('tools.title')}
              </h3>
              <ul className="space-y-2">
                <li>
                  <Link
                    href="/tool/business-name-generator"
                    className="text-white/75 hover:text-[#FDD577] flex items-center transition-all duration-300 text-[13px] leading-[1.5] font-normal hover:translate-x-1"
                  >
                    {t('tools.business_name_generator')}
                  </Link>
                </li>
                <li>
                  <Link
                    href="/tool/business-plan-generator"
                    className="text-white/75 hover:text-[#FDD577] flex items-center transition-all duration-300 text-[13px] leading-[1.5] font-normal hover:translate-x-1"
                  >
                    {t('tools.business_plan_generator')}
                  </Link>
                </li>
                <li>
                  <Link
                    href="/tool/blog-generator"
                    className="text-white/75 hover:text-[#FDD577] flex items-center transition-all duration-300 text-[13px] leading-[1.5] font-normal hover:translate-x-1"
                  >
                    {t('tools.blog_generator')}
                  </Link>
                </li>
                <li>
                  <Link
                    href="/tool/ebook-generator"
                    className="text-white/75 hover:text-[#FDD577] flex items-center transition-all duration-300 text-[13px] leading-[1.5] font-normal hover:translate-x-1"
                  >
                    {t('tools.ebook_generator')}
                  </Link>
                </li>
                <li>
                  <Link
                    href="/tool/market-research"
                    className="text-white/75 hover:text-[#FDD577] flex items-center transition-all duration-300 text-[13px] leading-[1.5] font-normal hover:translate-x-1"
                  >
                    {t('tools.market_research')}
                  </Link>
                </li>
                <li>
                  <Link
                    href="/tool/social-media-post-generator"
                    className="text-white/75 hover:text-[#FDD577] flex items-center transition-all duration-300 text-[13px] leading-[1.5] font-normal hover:translate-x-1"
                  >
                    {t('tools.social_media')}
                  </Link>
                </li>
                <li>
                  <Link
                    href="/tool/visual-design-generator"
                    className="text-white/75 hover:text-[#FDD577] flex items-center transition-all duration-300 text-[13px] leading-[1.5] font-normal hover:translate-x-1"
                  >
                    {t('tools.visual_design_generator')}
                  </Link>
                </li>
              </ul>
            </div>

            {/* Company and Business */}
            <div className="lg:col-span-1">
              <h3 className="text-[13px] font-semibold mb-3 text-white/95 tracking-wide letter-spacing-wider">
                {t('company_business')}
              </h3>
              <ul className="space-y-2">
                <li>
                  <Link
                    href={'/about-heyboss'}
                    className="text-white/75 hover:text-[#FDD577] flex items-center transition-all duration-300 text-[13px] leading-[1.5] font-normal hover:translate-x-1"
                  >
                    {t('about')}
                  </Link>
                </li>
                <li>
                  <Link
                    href="/pricing"
                    className="text-white/75 hover:text-[#FDD577] flex items-center transition-all duration-300 text-[13px] leading-[1.5] font-normal hover:translate-x-1"
                  >
                    {t('pricing')}
                  </Link>
                </li>
                <li>
                  <Link
                    href="/blog"
                    className="text-white/75 hover:text-[#FDD577] flex items-center transition-all duration-300 text-[13px] leading-[1.5] font-normal hover:translate-x-1"
                  >
                    {t('blog')}
                  </Link>
                </li>
                <li>
                  <Link
                    href={getLocalizedPath(locale, '/changelog')}
                    className="text-white/75 hover:text-[#FDD577] flex items-center transition-all duration-300 text-[13px] leading-[1.5] font-normal hover:translate-x-1"
                  >
                    {t('changelog')}
                  </Link>
                </li>
                <li>
                  <Link
                    href="/app-market"
                    className="text-white/75 hover:text-[#FDD577] flex items-center transition-all duration-300 text-[13px] leading-[1.5] font-normal hover:translate-x-1"
                  >
                    {t('appMarket')}
                  </Link>
                </li>
                <li>
                  <Link
                    href="/media-kit"
                    className="text-white/75 hover:text-[#FDD577] flex items-center transition-all duration-300 text-[13px] leading-[1.5] font-normal hover:translate-x-1"
                  >
                    {t('mediaKit')}
                  </Link>
                </li>
              </ul>
            </div>

            {/* Legal and Support Links */}
            <div className="lg:col-span-1">
              <h3 className="text-[13px] font-semibold mb-3 text-white/95 tracking-wide letter-spacing-wider">
                {t('legal_support')}
              </h3>
              <ul className="space-y-2">
                <li>
                  <Link
                    href="/legal/terms-conditions"
                    className="text-white/75 hover:text-[#FDD577] flex items-center transition-all duration-300 text-[13px] leading-[1.5] font-normal hover:translate-x-1"
                  >
                    {t('terms')}
                  </Link>
                </li>
                <li>
                  <Link
                    href="/legal/privacy-policy"
                    className="text-white/75 hover:text-[#FDD577] flex items-center transition-all duration-300 text-[13px] leading-[1.5] font-normal hover:translate-x-1"
                  >
                    {t('privacy')}
                  </Link>
                </li>
                <li>
                  <a
                    href={HELP_URL}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-white/75 hover:text-[#FDD577] flex items-center transition-all duration-300 text-[13px] leading-[1.5] font-normal hover:translate-x-1"
                  >
                    {t('helpCenter')}
                  </a>
                </li>
                <li>
                  <GetFreeCreditLink />
                </li>
              </ul>
            </div>
          </div>

          {/* Bottom Section - Merged with footer */}
          <div className="mt-6 md:mt-6 pt-4 md:pt-4 border-t border-white/5 flex flex-col sm:flex-row justify-between items-center gap-3">
            <div className="flex flex-col sm:flex-row items-center gap-2.5 sm:gap-4 w-full sm:w-auto">
              <LanguageSelector />
              <div className="flex items-center gap-2">
                <div className="relative">
                  <div className="w-1.5 h-1.5 rounded-full bg-green-500 animate-pulse shadow-sm shadow-green-500/40" />
                  <div className="absolute inset-0 w-1.5 h-1.5 rounded-full bg-green-500 animate-ping opacity-20" />
                </div>
                <span className="text-white/65 text-[12px] font-normal leading-tight tracking-wide">
                  {t('system_status')}
                </span>
              </div>
            </div>
            <p className="text-white/65 text-[12px] text-center sm:text-right w-full sm:w-auto font-normal leading-tight tracking-wide">
              © {new Date().getFullYear()} Heyboss.AI
            </p>
          </div>
        </div>
      </div>
    </footer>
  )
}
